// src/portal-app/team/RoleForms.js
//
// Every WRITE path on the Roles + Users surface, in one module:
//   RoleFormSheet       — add / edit a role
//   UserFormSheet       — add / edit a credential
//   ResetPasswordSheet  — reset a credential's password
//
// Extracted from RolesScreen.js in the Plan 4 Task 20 commit (b61bf97) — see
// rolesShared.js for why the file was split and along which boundaries, and
// RolesScreen.js's "FILE SPLIT" header note for why this went into the same
// commit as the P4 migration rather than a separate extraction commit first.
// This module itself IS a verbatim move: every payload shape and every
// testid on these three forms is byte-identical to the pre-split screen —
// diff this file's bodies against
// `git show b61bf97^:frontend/src/portal-app/team/RolesScreen.js` (the
// RoleFormSheet/UserFormSheet/ResetPasswordSheet functions there) to verify.
// A changed argument shape here can silently grant or revoke access (this
// screen is the permission editor).
import React, { useState, useEffect } from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { useEscape } from './rolesShared';

/* ------------------------------------------------------------------ */
/* RoleFormSheet — add / edit role                                     */
/* ------------------------------------------------------------------ */
export function RoleFormSheet({ open, onClose, onSaved, initial, t }) {
    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setName(initial?.name || '');
            setDesc(initial?.description || '');
            setError('');
        }
    }, [open, initial]);

    useEscape(open, onClose);

    if (!open) return null;

    const handleSubmit = async () => {
        const trimmed = name.trim();
        if (!trimmed) { setError(t('name_is_required', {}, 'Name is required')); return; }
        setError('');
        setSaving(true);
        try {
            await onSaved({ name: trimmed, description: desc.trim() });
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed');
            setError(msg);
            setSaving(false);
        }
    };

    return (
        <div
            className="rl-sheet-backdrop rl-backdrop-enter"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <GlassCard
                veil
                className="rl-sheet rl-sheet-enter"
                role="dialog"
                aria-modal="true"
                aria-label={initial ? t('edit_role', {}, 'Edit Role') : t('add_role', {}, 'Add Role')}
            >
                <div className="rl-drag" aria-hidden="true" />
                <div className="rl-sheet-title">
                    {initial ? t('edit_role', {}, 'Edit Role') : t('add_role', {}, 'Add Role')}
                </div>
                {error && <div className="rl-error">{error}</div>}

                <div className="rl-field">
                    <label className="rl-field-label">
                        {t('role_name', {}, 'Role Name')}
                        {' '}*
                    </label>
                    <input
                        className="rl-field-input"
                        data-testid="role-name-input"
                        type="text"
                        value={name}
                        placeholder={t('role_name_placeholder', {}, 'e.g. Supervisor')}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div className="rl-field">
                    <label className="rl-field-label">
                        {t('description', {}, 'Description')}
                    </label>
                    <input
                        className="rl-field-input"
                        data-testid="role-desc-input"
                        type="text"
                        value={desc}
                        placeholder={t('role_desc_placeholder', {}, 'Brief description')}
                        onChange={(e) => setDesc(e.target.value)}
                    />
                </div>

                <div className="rl-actions">
                    <button type="button" className="rl-btn-cancel" onClick={onClose}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="rl-btn-save"
                        data-testid="role-form-submit"
                        disabled={saving}
                        onClick={handleSubmit}
                    >
                        {saving && <span className="rl-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* UserFormSheet — add / edit credential                              */
/* ------------------------------------------------------------------ */
export function UserFormSheet({ open, mode, onClose, onSaved, initial, roles, t }) {
    const isCreate = mode === 'create';
    const blank = {
        username: '', password: '', display_name: '',
        short_id: '', role_id: '',
    };
    const [form, setForm] = useState(blank);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setForm(isCreate ? blank : {
                username: initial?.username || '',
                password: '',
                display_name: initial?.display_name || '',
                short_id: initial?.short_id || '',
                role_id: String(initial?.role_id || ''),
            });
            setError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode]);

    useEscape(open, onClose);
    if (!open) return null;

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const handleSubmit = async () => {
        if (isCreate && !form.username.trim()) {
            setError(t('username_required', {}, 'Username is required'));
            return;
        }
        if (isCreate && !form.password.trim()) {
            setError(t('password_required', {}, 'Password is required'));
            return;
        }
        // Role is REQUIRED on create. The select defaults to '' ("Select
        // role"), and the payload below drops role_id when it is blank — which
        // used to create a credential with role_id NULL. That is not an
        // unprivileged account: authenticate_global_user names any role-less
        // credential "admin" (backend/app/services/auth.py:56-60), so leaving
        // this select untouched silently minted a tenant owner. The backend now
        // rejects it (TenantCredentialCreate.role_id is required); this check
        // turns that 422 into the same inline message as the two fields above
        // rather than a raw server error.
        if (isCreate && !form.role_id) {
            setError(t('role_required', {}, 'Role is required'));
            return;
        }
        setError('');
        setSaving(true);
        try {
            const payload = isCreate
                ? {
                    username: form.username.trim(),
                    password: form.password.trim(),
                    display_name: form.display_name.trim() || form.username.trim(),
                    short_id: form.short_id.trim() || undefined,
                    role_id: form.role_id ? Number(form.role_id) : undefined,
                }
                : {
                    display_name: form.display_name.trim(),
                    role_id: form.role_id ? Number(form.role_id) : undefined,
                };
            await onSaved(payload);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed');
            setError(msg);
            setSaving(false);
        }
    };

    return (
        <div
            className="rl-sheet-backdrop rl-backdrop-enter"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <GlassCard
                veil
                className="rl-sheet rl-sheet-enter"
                role="dialog"
                aria-modal="true"
                aria-label={isCreate ? t('add_user', {}, 'Add User') : t('edit_user', {}, 'Edit User')}
            >
                <div className="rl-drag" aria-hidden="true" />
                <div className="rl-sheet-title">
                    {isCreate ? t('add_user', {}, 'Add User') : t('edit_user', {}, 'Edit User')}
                </div>
                {error && <div className="rl-error">{error}</div>}

                {isCreate && (
                    <div className="rl-field">
                        <label className="rl-field-label">{t('username', {}, 'Username')} *</label>
                        <input
                            className="rl-field-input"
                            data-testid="cred-username-input"
                            type="text"
                            value={form.username}
                            autoComplete="off"
                            onChange={(e) => set('username', e.target.value)}
                        />
                    </div>
                )}

                {isCreate && (
                    <div className="rl-field">
                        <label className="rl-field-label">{t('password', {}, 'Password')} *</label>
                        <input
                            className="rl-field-input"
                            data-testid="cred-password-input"
                            type="password"
                            value={form.password}
                            autoComplete="new-password"
                            onChange={(e) => set('password', e.target.value)}
                        />
                    </div>
                )}

                <div className="rl-field">
                    <label className="rl-field-label">{t('display_name', {}, 'Display Name')}</label>
                    <input
                        className="rl-field-input"
                        data-testid="cred-display-name-input"
                        type="text"
                        value={form.display_name}
                        onChange={(e) => set('display_name', e.target.value)}
                    />
                </div>

                {isCreate && (
                    <div className="rl-field">
                        <label className="rl-field-label">{t('short_id', {}, 'Short ID')}</label>
                        <input
                            className="rl-field-input rl-num"
                            data-testid="cred-short-id-input"
                            type="text"
                            inputMode="numeric"
                            value={form.short_id}
                            maxLength={6}
                            onChange={(e) => set('short_id', e.target.value)}
                        />
                    </div>
                )}

                <div className="rl-field">
                    <label className="rl-field-label">{t('role', {}, 'Role')}</label>
                    <select
                        className="rl-field-input rl-field-select"
                        data-testid="cred-role-select"
                        value={form.role_id}
                        onChange={(e) => set('role_id', e.target.value)}
                    >
                        <option value="">{t('select_role', {}, 'Select role')}</option>
                        {roles.map((r) => (
                            <option key={r.id} value={String(r.id)}>{r.name}</option>
                        ))}
                    </select>
                </div>

                <div className="rl-actions">
                    <button type="button" className="rl-btn-cancel" onClick={onClose}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="rl-btn-save"
                        data-testid="cred-form-submit"
                        disabled={saving}
                        onClick={handleSubmit}
                    >
                        {saving && <span className="rl-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* ResetPasswordSheet                                                  */
/* ------------------------------------------------------------------ */
export function ResetPasswordSheet({ open, onClose, onSaved, t }) {
    const [newPass, setNewPass] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) { setNewPass(''); setError(''); }
    }, [open]);

    useEscape(open, onClose);
    if (!open) return null;

    const handleSubmit = async () => {
        const trimmed = newPass.trim();
        if (!trimmed) { setError(t('password_required', {}, 'Password is required')); return; }
        setError('');
        setSaving(true);
        try {
            await onSaved(trimmed);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed');
            setError(msg);
            setSaving(false);
        }
    };

    return (
        <div
            className="rl-sheet-backdrop rl-backdrop-enter"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <GlassCard
                veil
                className="rl-sheet rl-sheet-enter"
                role="dialog"
                aria-modal="true"
            >
                <div className="rl-drag" aria-hidden="true" />
                <div className="rl-sheet-title">{t('reset_password', {}, 'Reset Password')}</div>
                {error && <div className="rl-error">{error}</div>}

                <div className="rl-field">
                    <label className="rl-field-label">{t('new_password', {}, 'New Password')} *</label>
                    <input
                        className="rl-field-input"
                        data-testid="new-password-input"
                        type="password"
                        value={newPass}
                        autoComplete="new-password"
                        onChange={(e) => setNewPass(e.target.value)}
                    />
                </div>

                <div className="rl-actions">
                    <button type="button" className="rl-btn-cancel" onClick={onClose}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="rl-btn-save"
                        data-testid="reset-pass-submit"
                        disabled={saving}
                        onClick={handleSubmit}
                    >
                        {saving && <span className="rl-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}
